import Papa from 'papaparse';

export const FRESHA_SERVICE_COLUMNS = {
  NAME: 0,
  PRICE: 1,
  DURATION: 2,
  DESCRIPTION: 5,
  CATEGORY: 6,
  ONLINE_BOOKING: 9,
  SERVICE_ID: 13,
};

const detectDelimiter = (text) => {
  const firstLine = (text.split(/\r?\n/).find((line) => line.trim()) || '');
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return semicolons >= commas ? ';' : ',';
};

const normalizeWhitespace = (value) => (value == null ? '' : String(value).trim());

const parseDurationMinutes = (rawValue) => {
  const raw = normalizeWhitespace(rawValue);
  if (!raw) {
    return { minutes: null, errorKey: 'dataImport.services.errors.durationRequired' };
  }

  const hourMatch = raw.match(/(\d+)\s*h/i);
  const minuteMatch = raw.match(/(\d+)\s*m/i);
  const hours = hourMatch ? Number.parseInt(hourMatch[1], 10) : 0;
  const minutes = minuteMatch ? Number.parseInt(minuteMatch[1], 10) : 0;
  const totalMinutes = (hours * 60) + minutes;

  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return { minutes: null, errorKey: 'dataImport.services.errors.durationInvalid' };
  }

  return { minutes: totalMinutes, errorKey: null };
};

const parsePrice = (rawValue) => {
  const raw = normalizeWhitespace(rawValue);
  if (!raw) {
    return { price: 0, warningKey: 'dataImport.services.warnings.missingPrice' };
  }

  const normalized = raw.replace(',', '.');
  const price = Number.parseFloat(normalized);
  if (!Number.isFinite(price) || price < 0) {
    return { price: 0, warningKey: 'dataImport.services.warnings.invalidPrice' };
  }

  return { price, warningKey: null };
};

const isOnlineBookingDisabled = (rawValue) => {
  const normalized = normalizeWhitespace(rawValue).toLowerCase();
  return normalized === 'disabled' || normalized === 'desativado';
};

const buildRowFromValues = (values, rowNumber) => {
  const getValue = (index) => normalizeWhitespace(values[index]);
  const errors = [];
  const warnings = [];

  const freshaServiceId = getValue(FRESHA_SERVICE_COLUMNS.SERVICE_ID);
  const name = getValue(FRESHA_SERVICE_COLUMNS.NAME);
  const description = getValue(FRESHA_SERVICE_COLUMNS.DESCRIPTION);
  const categoryName = getValue(FRESHA_SERVICE_COLUMNS.CATEGORY);
  const onlineBookingRaw = getValue(FRESHA_SERVICE_COLUMNS.ONLINE_BOOKING);

  if (!freshaServiceId) {
    errors.push('dataImport.services.errors.serviceIdRequired');
  }

  if (!name) {
    errors.push('dataImport.services.errors.nameRequired');
  }

  const durationResult = parseDurationMinutes(getValue(FRESHA_SERVICE_COLUMNS.DURATION));
  if (durationResult.errorKey) {
    errors.push(durationResult.errorKey);
  }

  const priceResult = parsePrice(getValue(FRESHA_SERVICE_COLUMNS.PRICE));
  if (priceResult.warningKey) {
    warnings.push(priceResult.warningKey);
  }

  return {
    rowNumber,
    freshaServiceId,
    name,
    description,
    categoryName,
    durationMinutes: durationResult.minutes,
    price: priceResult.price,
    disabled: isOnlineBookingDisabled(onlineBookingRaw),
    errors,
    warnings,
    rawValues: values,
  };
};

export const revalidateServiceRow = (row) => {
  const draft = {
    freshaServiceId: normalizeWhitespace(row.freshaServiceId),
    name: normalizeWhitespace(row.name),
    description: normalizeWhitespace(row.description),
    categoryName: normalizeWhitespace(row.categoryName),
    durationMinutes: row.durationMinutes,
    price: row.price,
    disabled: Boolean(row.disabled),
  };

  const errors = [];
  const warnings = [...(row.warnings || [])];

  if (!draft.freshaServiceId) {
    errors.push('dataImport.services.errors.serviceIdRequired');
  }

  if (!draft.name) {
    errors.push('dataImport.services.errors.nameRequired');
  }

  if (!Number.isFinite(draft.durationMinutes) || draft.durationMinutes <= 0) {
    errors.push('dataImport.services.errors.durationInvalid');
  }

  if (!Number.isFinite(draft.price) || draft.price < 0) {
    draft.price = 0;
    if (!warnings.includes('dataImport.services.warnings.invalidPrice')) {
      warnings.push('dataImport.services.warnings.invalidPrice');
    }
  }

  return {
    ...row,
    ...draft,
    errors,
    warnings,
  };
};

export const parseFreshaServiceCsv = (text) => {
  if (!text || !text.trim()) {
    return { rows: [], headers: [], delimiter: ',', errorKey: 'dataImport.services.errors.emptyFile' };
  }

  const delimiter = detectDelimiter(text);
  const parsed = Papa.parse(text, {
    delimiter,
    skipEmptyLines: false,
  });

  if (parsed.errors?.length) {
    return { rows: [], headers: [], delimiter, errorKey: 'dataImport.services.errors.parseFailed' };
  }

  const dataRows = (parsed.data || []).filter((row) => Array.isArray(row) && row.some((cell) => normalizeWhitespace(cell)));
  if (dataRows.length === 0) {
    return { rows: [], headers: [], delimiter, errorKey: 'dataImport.services.errors.emptyFile' };
  }

  let startIndex = 0;
  if (normalizeWhitespace(dataRows[0][0]).toUpperCase() === 'SEP=') {
    startIndex = 1;
  }

  const headerRow = dataRows[startIndex] || [];
  const contentRows = dataRows.slice(startIndex + 1);
  const rows = contentRows
    .map((values, index) => buildRowFromValues(values, startIndex + index + 2))
    .filter((row) => row.freshaServiceId || row.name || row.errors.length > 0);

  if (rows.length === 0) {
    return { rows: [], headers: headerRow, delimiter, errorKey: 'dataImport.services.errors.emptyFile' };
  }

  return { rows, headers: headerRow, delimiter, errorKey: null };
};

export const buildImportServicesPayload = (rows) => ({
  services: rows.map((row) => ({
    freshaServiceId: row.freshaServiceId,
    name: row.name,
    durationMinutes: row.durationMinutes,
    price: row.price,
    description: row.description || null,
    categoryName: row.categoryName || null,
    disabled: Boolean(row.disabled),
  })),
});

export const buildFailingServicesCsv = (rows, headers, delimiter) => {
  const headerLine = (headers || []).join(delimiter);
  const body = rows.map((row) => (row.rawValues || []).map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(delimiter));
  return [headerLine, ...body].join('\n');
};

export const countUniqueCategories = (rows) => {
  const names = rows
    .map((row) => normalizeWhitespace(row.categoryName))
    .filter(Boolean);
  return new Set(names.map((name) => name.toLowerCase())).size;
};
